import { Component, OnInit, OnDestroy } from '@angular/core';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { GiftCertificatesService } from 'src/app/services/gift-certificates.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { Router } from '@angular/router';
import { ReportsService } from 'src/app/services/report.service';
import { GeneralService } from 'src/app/services/general.service';
import { IReportResponse } from 'src/app/Shared/model/ReportsModel';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { PosSalesService } from 'src/app/services/possalesservice';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-ops-gift-certificates',
  standalone: true,
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, UserPermissionHandlerDirective, LoaderComponent, TooltipModule],
  templateUrl: './ops-gift-certificates.html',
  styleUrl: './ops-gift-certificates.scss'
})
export class OpsGiftCertificates implements OnInit, OnDestroy {
  isCollapsed = true;
  certificates: any[] = [];
  allCertificates: any[] = [];
  selectedDetails: any = null;
  giftCertInput: string = '';
  giftCertValue: string = '';
  private destroy$ = new Subject<void>();
  statusFilter: 'Active' | 'Inactive' | 'Both' = 'Active';
  selectedGiftCertNo: string | null = null;
  public loaderIcon: Promise<boolean>;
  reportUrl: any;
  marketId: any;
  renewGCDetails: any = null;
  newExpiryDate: any = null;
  minExpiryDate = new Date();
  isAntiquesVillage = false;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  today: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );
  pdfUrl: SafeResourceUrl | null = null;

  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.GiftCertificates, Action: EActionTypes.VIEW };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.GiftCertificates, Action: EActionTypes.EDIT };

  constructor(
    private giftService: GiftCertificatesService,
    private posSalesService: PosSalesService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private router: Router,
    private service: ReportsService,
    private generalService: GeneralService
  ) { }

  ngOnInit(): void {
    this.getReportUrl();
    this.getDefaultMarketId();
    this.loadCustomMarketSettings();
  }

  getReportUrl() {
    this.generalService.getReportUrl().subscribe((response: any) => {
      this.reportUrl = response;
    });
  }

  getDefaultMarketId() {
    this.giftService.getDefaultMarketId().subscribe((res: any) => {
      if (res) {
        this.marketId = res;
      }
    });
  }

  loadCustomMarketSettings(): void {
    this.generalService.getCustomMarketSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isAntiquesVillage = res?.AntiqueVillageChanges ?? false;
          this.initializeDateFilters();
          this.loadCertificates(this.statusFilter);
        },
        error: (err) => {
          console.error('Failed to load custom market settings:', err);
          this.isAntiquesVillage = false;
          this.loadCertificates(this.statusFilter);
        }
      });
  }

  private initializeDateFilters(): void {
    if (this.isAntiquesVillage) {
      this.fromDate = new Date(this.today);
      this.toDate = new Date(this.today);
    } else {
      this.fromDate = null;
      this.toDate = null;
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  loadCertificates(status: string): void {
    const today = this.formatDate(new Date());

    this.giftService.getGiftCertificates(today, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.allCertificates = Array.isArray(data) ? data : [];
          this.certificates = [...this.allCertificates];
        },
        error: (err) => {
          if (err && err.status && err.status !== 200) {
            this.toastr.error(
              $localize`Error loading gift certificates`,
              $localize`Error`,
              this.sharedHTMLSettings.ToasterConfig
            );
          }

          this.certificates = [];
        }
      });
  }

  trackCertificate(giftCertNo: string) {
    this.giftService.getGiftCertificateDetails(giftCertNo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: details => {
          this.selectedDetails = details;

          const modalEl = document.getElementById('giftCertificateModal');
          if (modalEl) {
            let modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);
            if (!modalInstance) {
              modalInstance = new (window as any).bootstrap.Modal(modalEl);
            }
            modalInstance.show();
          }
        },
        error: () => {
          this.toastr.error(
            $localize`Error fetching certificate details`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  onGiftCertEnter() {
    const gcNo = this.giftCertInput?.trim();
    if (!gcNo) {
      this.toastr.warning(
        $localize`Please enter a Gift Certificate number.`,
        $localize`Warning`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    this.fetchGiftCertificate(gcNo);
  }

  onRowSelected(args: any): void {
    const rowData = args?.data;
    if (!rowData) return;

    const gcNo = rowData.GiftCertificateNo || rowData.giftCertificateNo || null;
    this.selectedGiftCertNo = gcNo;
    this.giftCertValue = gcNo || '';

    // Call API and open modal after response is loaded
    this.fetchGiftCertificate(gcNo);
  }

  onRowSelecting(args: any): void {
    const cell = args.target?.closest('td');

    if (!cell) {
      return;
    }

    const grid = cell.closest('.e-grid');

    if (!grid) {
      return;
    }

    const columnIndex = cell.cellIndex;
    const headerCell = grid.querySelectorAll('.e-headercell')[columnIndex];

    const headerText = headerCell?.textContent?.trim();

    if (headerText === 'View' || headerText === 'Renew') {
      args.cancel = true;
    }
  }

  onGiftCertificateSearch(): void {
    const searchValue = this.giftCertInput?.trim().toLowerCase() || '';

    if (!searchValue) {
      this.certificates = [...this.allCertificates];
      return;
    }

    const filteredCertificates = this.allCertificates.filter(x =>
      (x.GiftCertificateNo || x.giftCertificateNo || '')
        .toString()
        .toLowerCase()
        .includes(searchValue)
    );

    if (filteredCertificates.length === 0) {
      this.toastr.info('Gift Certificate not found.', 'Not Found', this.sharedHTMLSettings.ToasterConfig);
      this.certificates = [...this.allCertificates];
      return;
    }

    this.certificates = filteredCertificates;
  }

  onGiftCertificateInputChange(value: string): void {
    if (!value?.trim()) {
      this.certificates = [...this.allCertificates];
    }
  }

  onView(row: any) {
    const invoiceNum = row?.GCInvoiceNo || row?.gCInvoiceNo;
    const apiCall = this.displayGiftCertificate(invoiceNum);

    this.loaderIcon = Promise.resolve(true);
    this.pdfUrl = null;

    apiCall.subscribe({
      next: (response: any) => {
        const pdfDataUrl = response?.Message;

        if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
          Swal.fire('Error', 'Invalid PDF data in response', 'error');
          this.loaderIcon = Promise.resolve(false);
          return;
        }

        const pdfBase64 = pdfDataUrl.split(',')[1];
        const blob = this.base64ToBlob(pdfBase64, 'application/pdf');
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));

        this.loaderIcon = Promise.resolve(false);

        const modalEl = document.getElementById('viewReceiptModal');
        if (modalEl) {
          const modalInstance = (window as any)['bootstrap'].Modal.getOrCreateInstance(modalEl);
          modalInstance.show();
        }
      },

      error: () => {
        Swal.fire('Error', 'Failed to load receipt for view', 'error');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  private base64ToBlob(base64: string, type: string): Blob {
    const byteCharacters = atob(base64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([byteArray], { type });
  }

  displayGiftCertificate(invoiceNo: number) {
    const params = {
      InvsNumber: invoiceNo,
      SalesReceiptid: 0
    };
    return this.posSalesService.generateGCReceipt(params);
  }

  onCheckboxChange(rowData: any, event: any) {
    const checked = !!event?.target?.checked;
    const gcNo = rowData?.GiftCertificateNo || rowData?.giftCertificateNo || null;

    if (checked) {
      this.selectedGiftCertNo = gcNo;
      this.giftCertInput = gcNo || '';
    } else {
      if (this.selectedGiftCertNo === gcNo) {
        this.selectedGiftCertNo = null;
        this.giftCertInput = '';
      }
    }
  }

  private fetchGiftCertificate(gcNo: string) {
    if (this.statusFilter !== 'Both') {
      const matchedCertificate = this.certificates.find(
        x => x.GiftCertificateNo?.toString().trim().toUpperCase()
          === gcNo.trim().toUpperCase()
      );

      if (!matchedCertificate) {
        Swal.fire({
          title: 'Error',
          text: 'Gift Certificate # does not exist',
          icon: 'error',
          confirmButtonText: 'OK',
          allowOutsideClick: false
        });

        this.selectedDetails = null;
        return;
      }
    }

    this.loaderIcon = Promise.resolve(true);
    this.giftService.getGiftCertificateDetails(gcNo).pipe(takeUntil(this.destroy$)).subscribe({
      next: details => {
        if (details) {
          this.selectedDetails = {
            giftCertificateNo: details.GiftCertificateNo,
            startDate: details.StartDate,
            expiryDate: details.ExpiryDate,
            initialAmt: details.InitialAmt,
            balance: details.Balance,
            customerName: details.CustomerName,
            address: details.Address,
            generatedBy: details.GeneratedBy,
            invoices: (details.Invoices || []).map((inv: any) => ({
              invoiceNo: inv.InvoiceNo,
              date: inv.Date,
              invoiceAmount: inv.InvoiceAmount,
              gcAmount: inv.GCAmount,
              balanceAmount: inv.BalanceAmount
            }))
          };
          this.loaderIcon = Promise.resolve(false);
          const modalEl = document.getElementById('giftCertificateModal');
          if (modalEl) {
            let modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);
            if (!modalInstance) {
              modalInstance = new (window as any).bootstrap.Modal(modalEl);
            }
            modalInstance.show();
          }
        } else {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error($localize`Gift Certificate # does not exist`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
          this.selectedDetails = null;
        }
      },
      error: () => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error(
          $localize`Gift Certificate # does not exist`,
          $localize`Error`,
          this.sharedHTMLSettings.ToasterConfig
        );
        this.selectedDetails = null;
      }
    });
  }

  resetModal() {
    this.selectedDetails = null;
    this.giftCertInput = '';
    this.selectedGiftCertNo = null;
  }

  onStatusChange(status: 'Active' | 'Inactive' | 'Both') {
    this.allCertificates = [];
    this.certificates = [];
    this.statusFilter = status;
    this.selectedGiftCertNo = null;
    this.giftCertInput = '';
    this.selectedDetails = null;
    this.loadCertificates(status);
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  closeGiftCertificates() {
    this.isCollapsed = true;
    this.router.navigate(['/dashboard']);
  }

  invokeReport(obj) {
    var openInNewTab = '_blank';
    this.loaderIcon = Promise.resolve(true);
    this.service.generateReport(obj).subscribe((response: IReportResponse) => {
      if (response.Success) {
        window.open(this.reportUrl + `report?id=` + response.ReportRequestId,
          openInNewTab, 'width=' + (screen.availWidth - 5) + ',height=' + (screen.availHeight - 5));
        this.loaderIcon = Promise.resolve(false);
      } else {
        console.log('Error on generating Operational Report');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onReportView(): void {
    this.loaderIcon = Promise.resolve(true);

    const today = this.formatDate(new Date());

    const obj = {
      ReportName: "GiftCertificatesReport",
      ReportParameters: {},
      MarketId: this.marketId
    };

    obj.ReportParameters["MarketId"] = this.marketId;
    obj.ReportParameters["Status"] = this.statusFilter;
    obj.ReportParameters["AsOfDate"] = today;
    obj.ReportParameters["FromDate"] = "";
    obj.ReportParameters["ToDate"] = "";

    if (this.isAntiquesVillage) {
      obj.ReportParameters["FromDate"] = this.fromDate
        ? this.formatDate(this.fromDate)
        : "";

      obj.ReportParameters["ToDate"] = this.toDate
        ? this.formatDate(this.toDate)
        : "";
    }

    this.invokeReport(obj);
  }

  onRenew(row: any) {
    this.renewGCDetails = row;
    this.newExpiryDate = null;

    const modalEl = document.getElementById('renewGCModal');
    if (modalEl) {
      let modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);
      if (!modalInstance) {
        modalInstance = new (window as any).bootstrap.Modal(modalEl);
      }
      modalInstance.show();
    }
  }

  closeRenewModal() {
    const modalEl = document.getElementById('renewGCModal');
    if (modalEl) {
      let modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);

      if (!modalInstance) {
        modalInstance = new (window as any).bootstrap.Modal(modalEl);
      }

      modalInstance.hide();
    }

    this.renewGCDetails = null;
    this.newExpiryDate = null;
  }

  renewGiftCertificate() {
    if (!this.newExpiryDate) {
      this.toastr.warning(
        $localize`Please enter the modified Expiry Date.`,
        $localize`Warning`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const expiryDate = new Date(this.newExpiryDate);
    const today = new Date();

    // Compare date only
    expiryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (expiryDate < today) {
      this.toastr.error(
        $localize`Expiry Date cannot be earlier than today's date.`,
        $localize`Error`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const payload = {
      GiftCertificateNo: this.renewGCDetails.GiftCertificateNo,
      NewExpiryDate: this.newExpiryDate
    };

    this.giftService.renewGiftCertificate(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.Success) {
            this.toastr.success(
              response.message || $localize`Expiry date updated successfully.`,
              $localize`Success`,
              this.sharedHTMLSettings.ToasterConfig
            );

            this.closeRenewModal();

            this.loadCertificates(this.statusFilter);
          } else {
            this.toastr.error(
              response?.message || $localize`Could not update expiry date.`,
              $localize`Error`,
              this.sharedHTMLSettings.ToasterConfig
            );
          }
        },
        error: () => {
          this.toastr.error(
            $localize`An error occurred while updating the expiry date.`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  onExpiryDateInput(event: any): void {
    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/\D/g, '');

    // Limit to 8 digits
    value = value.substring(0, 8);

    if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }

    if (value.length > 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    input.value = value;

    // Validate only when complete date is entered
    if (value.length === 10) {
      const month = Number(value.substring(0, 2));
      const day = Number(value.substring(3, 5));
      const year = Number(value.substring(6, 10));

      // Basic month/year validation
      if (month < 1 || month > 12 || year < 1 || year > 2099) {
        input.value = '';
        this.newExpiryDate = null;
        return;
      }

      // Get number of days in the specified month/year
      const daysInMonth = new Date(year, month, 0).getDate();
      if (day < 1 || day > daysInMonth) {
        setTimeout(() => {
          input.value = '';
          this.newExpiryDate = null;
        }, 400);
        return;
      }
    }
  }

  onBeforeOpen(args: any): void {
    if (!args?.target?.dataset?.content) args.cancel = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
